import React, { useState, useEffect } from "react";
import { useLanguage } from "../App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { coursesData } from "../data/coursesData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUCCESS_TEXTS = {
  en: {
    title: "Booking Request Sent!",
    message: "Thanks for booking with DAKHLA KITE PLANET! We've received your request and will contact you shortly to confirm the details.",
    refLabel: "Your Booking Reference",
    close: "Close",
  },
  fr: {
    title: "Demande de réservation envoyée !",
    message: "Merci d'avoir réservé avec DAKHLA KITE PLANET ! Nous avons bien reçu votre demande et nous vous contacterons rapidement pour confirmer les détails.",
    refLabel: "Votre référence de réservation",
    close: "Fermer",
  },
  es: {
    title: "¡Solicitud de reserva enviada!",
    message: "¡Gracias por reservar con DAKHLA KITE PLANET! Hemos recibido su solicitud y nos pondremos en contacto con usted en breve para confirmar los detalles.",
    refLabel: "Su referencia de reserva",
    close: "Cerrar",
  },
  de: {
    title: "Buchungsanfrage gesendet!",
    message: "Danke für Ihre Buchung bei DAKHLA KITE PLANET! Wir haben Ihre Anfrage erhalten und werden Sie in Kürze kontaktieren, um die Details zu bestätigen.",
    refLabel: "Ihre Buchungsreferenz",
    close: "Schließen",
  },
};

const SUBMIT_TEXTS = {
  en: { submit: "Send Booking Request", sending: "Sending..." },
  fr: { submit: "Envoyer la demande de réservation", sending: "Envoi..." },
  es: { submit: "Enviar solicitud de reserva", sending: "Enviando..." },
  de: { submit: "Buchungsanfrage senden", sending: "Wird gesendet..." },
};

const BookingModal = ({ isOpen, onClose, selectedCourse }) => {
  const { t, language } = useLanguage();
  const [bookingType, setBookingType] = useState("course");
  const [startDate, setStartDate] = useState("");
  const [pricingIdx, setPricingIdx] = useState("");
  const [bookingReference, setBookingReference] = useState(null);
  const [formData, setFormData] = useState({
    courseId: "",
    packageType: "",
    guests: 1,
    name: "",
    email: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);

  const successTexts = SUCCESS_TEXTS[language] || SUCCESS_TEXTS.en;
  const submitTexts = SUBMIT_TEXTS[language] || SUBMIT_TEXTS.en;

  const packages = [
    { value: "premium", label: t.packages.premium.name, price: 1280 },
    { value: "nomades", label: t.packages.nomades.name, price: 940 },
    { value: "rental", label: t.packages.rental.name, price: 600 },
    { value: "surfLesson", label: t.packages.surfLesson.name, price: 980 },
    { value: "surfGuiding", label: t.packages.surfGuiding.name, price: 900 },
  ];

  const allCourses = [...coursesData.kitesurf, ...coursesData.surf];
  const currentCourse = allCourses.find((c) => c.id === formData.courseId);
  const pricingOptions = currentCourse?.pricing || [];
  const selectedPricing = pricingIdx !== "" ? pricingOptions[parseInt(pricingIdx)] : null;

  const pricingLabel = (opt) =>
    `${opt.hours ? `${opt.hours} hours` : opt.duration} - €${opt.price}${opt.unit ? ` / ${opt.unit}` : ""}`;

  useEffect(() => {
    if (selectedCourse) {
      setBookingType("course");
      setFormData(prev => ({ ...prev, courseId: selectedCourse.courseId }));
      const course = allCourses.find((c) => c.id === selectedCourse.courseId);
      if (course && selectedCourse.priceInfo) {
        const idx = course.pricing.findIndex((p) => p.price === selectedCourse.priceInfo.price);
        setPricingIdx(idx >= 0 ? String(idx) : "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse]);

  const calculateTotalPrice = () => {
    if (bookingType === "course" && selectedPricing) {
      return selectedPricing.price * formData.guests;
    } else if (bookingType === "package" && formData.packageType) {
      const selectedPackage = packages.find((p) => p.value === formData.packageType);
      return selectedPackage ? selectedPackage.price * formData.guests : 0;
    }
    return 0;
  };

  const resetAndClose = () => {
    setBookingReference(null);
    setStartDate("");
    setPricingIdx("");
    setFormData({ courseId: "", packageType: "", guests: 1, name: "", email: "", phone: "" });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (bookingType === "course" && !formData.courseId) {
      toast.error("Please select a course");
      return;
    }
    if (bookingType === "course" && !selectedPricing) {
      toast.error("Please select a duration");
      return;
    }
    if (bookingType === "package" && !formData.packageType) {
      toast.error("Please select a package");
      return;
    }
    if (!startDate) {
      toast.error("Please select a start date");
      return;
    }

    setLoading(true);

    try {
      const isoDate = new Date(startDate + "T12:00:00").toISOString();
      const packageLabel =
        bookingType === "package"
          ? formData.packageType
          : `${formData.courseId}${selectedPricing ? ` (${selectedPricing.hours ? selectedPricing.hours + "h" : selectedPricing.duration})` : ""}`;
      const bookingData = {
        package_type: packageLabel,
        lesson_type: bookingType,
        start_date: isoDate,
        end_date: isoDate,
        guests: formData.guests,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        language: language,
        total_price: calculateTotalPrice(),
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      setBookingReference(response.data.booking_reference);
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(t.booking.error);
    } finally {
      setLoading(false);
    }
  };

  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {bookingReference ? (
          <div className="py-8 text-center space-y-6" data-testid="booking-success-screen">
            <CheckCircle2 className="h-20 w-20 text-green-500 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-900">{successTexts.title}</h2>
            <p className="text-gray-600 text-lg leading-relaxed max-w-md mx-auto">
              {successTexts.message}
            </p>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 max-w-sm mx-auto">
              <p className="text-sm text-blue-700 mb-1">{successTexts.refLabel}</p>
              <p className="text-3xl font-bold text-blue-600 tracking-widest" data-testid="booking-reference">
                {bookingReference}
              </p>
            </div>
            <Button
              onClick={resetAndClose}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-10 py-5 text-lg"
              data-testid="booking-success-close-btn"
            >
              {successTexts.close}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                {t.booking.title}
              </DialogTitle>
              <DialogDescription className="sr-only">Booking request form</DialogDescription>
            </DialogHeader>

            <Tabs value={bookingType} onValueChange={setBookingType} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="course" data-testid="booking-tab-course">Lessons / Courses</TabsTrigger>
                <TabsTrigger value="package" data-testid="booking-tab-package">Accommodation Packages</TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit} className="space-y-6 mt-6">
                <TabsContent value="course" className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="course">Select Course *</Label>
                    <Select
                      value={formData.courseId}
                      onValueChange={(value) => {
                        setFormData({ ...formData, courseId: value });
                        setPricingIdx("");
                      }}
                    >
                      <SelectTrigger data-testid="booking-course-select">
                        <SelectValue placeholder="Choose a course" />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-500">
                          KITESURF LESSONS
                        </div>
                        {coursesData.kitesurf.map((course) => (
                          <SelectItem key={course.id} value={course.id}>
                            {course.name[language]}
                          </SelectItem>
                        ))}
                        <div className="px-2 py-1 text-xs font-semibold text-gray-500 mt-2">
                          SURF COURSES
                        </div>
                        {coursesData.surf.map((course) => (
                          <SelectItem key={course.id} value={course.id}>
                            {course.name[language]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedCourse && (
                      <p className="text-sm text-gray-600">
                        Selected: {selectedCourse.priceInfo.hours ? `${selectedCourse.priceInfo.hours} hours` : selectedCourse.priceInfo.duration} - €{selectedCourse.priceInfo.price}
                      </p>
                    )}
                  </div>

                  {/* Duration / Pricing option */}
                  {formData.courseId && pricingOptions.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="duration">Duration *</Label>
                      <Select value={pricingIdx} onValueChange={setPricingIdx}>
                        <SelectTrigger data-testid="booking-duration-select">
                          <SelectValue placeholder="Choose duration" />
                        </SelectTrigger>
                        <SelectContent>
                          {pricingOptions.map((opt, idx) => (
                            <SelectItem key={idx} value={String(idx)}>
                              {pricingLabel(opt)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="package" className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="package">{t.booking.selectPackage} *</Label>
                    <Select
                      value={formData.packageType}
                      onValueChange={(value) =>
                        setFormData({ ...formData, packageType: value })
                      }
                    >
                      <SelectTrigger data-testid="booking-package-select">
                        <SelectValue placeholder={t.booking.selectPackage} />
                      </SelectTrigger>
                      <SelectContent>
                        {packages.map((pkg) => (
                          <SelectItem key={pkg.value} value={pkg.value}>
                            {pkg.label} - {pkg.price}€
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>

                {/* Date Selection - native date input */}
                <div className="space-y-2">
                  <Label htmlFor="start-date">{bookingType === "package" ? t.booking.startDate : "Lesson Date"} *</Label>
                  <Input
                    id="start-date"
                    type="date"
                    min={todayStr}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                    data-testid="booking-date-input"
                  />
                </div>

                {/* Number of People */}
                <div className="space-y-2">
                  <Label htmlFor="guests">{bookingType === "package" ? t.booking.guests : "Number of Participants"}</Label>
                  <Input
                    id="guests"
                    type="number"
                    min="1"
                    max="10"
                    value={formData.guests}
                    onChange={(e) =>
                      setFormData({ ...formData, guests: parseInt(e.target.value) || 1 })
                    }
                    data-testid="booking-guests-input"
                  />
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">{t.booking.contactInfo}</h3>

                  <div className="space-y-2">
                    <Label htmlFor="name">{t.contact.name} *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      data-testid="booking-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t.contact.email} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      required
                      data-testid="booking-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">{t.contact.phone} *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      required
                      data-testid="booking-phone-input"
                    />
                  </div>
                </div>

                {/* Total Price */}
                {calculateTotalPrice() > 0 && (
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">{t.booking.totalPrice}:</span>
                      <span className="text-2xl font-bold text-blue-600" data-testid="booking-total-price">
                        €{calculateTotalPrice()}
                      </span>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white py-6 text-lg"
                  data-testid="booking-submit-btn"
                >
                  {loading ? submitTexts.sending : submitTexts.submit}
                </Button>
              </form>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
